import type { DamageType } from '../config/constants';

export type SymbiosisKind =
  | 'bullet'
  | 'missile'
  | 'salvo'
  | 'arc'
  | 'laser'
  | 'frost'
  | 'plasma'
  | 'cluster'
  | 'cryo'
  | 'support';

export interface SymbiosisDef {
  id: string;
  name: string;
  short: string;
  desc: string;
  cost: number;
  kind: SymbiosisKind;
  colour: number;
  damage: number;
  range: number;
  fireRate: number;
  damageType: DamageType;
  splash?: number;
  burnDps?: number;
  slowPct?: number;
  armourPen?: number;
  chain?: number;
  /** Support modules: passive buffs applied to the host tower. */
  buffRate?: number;
  buffRange?: number;
  /** Support modules: aura radius that buffs neighbouring towers. */
  auraRange?: number;
  auraRate?: number;
}

/** Bolt-on secondary weapons. Available once a tower reaches EXPERIMENTAL. */
export const SYMBIOSIS: Record<string, SymbiosisDef> = {
  pod_missile: {
    id: 'pod_missile',
    name: 'Missile Pod',
    short: 'MSL POD',
    desc: 'Guided explosive missile on an independent launcher.',
    cost: 90,
    kind: 'missile',
    colour: 0xffb347,
    damage: 34,
    range: 175,
    fireRate: 0.55,
    damageType: 'EXPLOSIVE',
    splash: 44,
    armourPen: 0.1,
  },
  pod_salvo: {
    id: 'pod_salvo',
    name: 'Missile Salvo Rack',
    short: 'SALVO',
    desc: 'Fires a four-missile salvo that saturates a cluster.',
    cost: 150,
    kind: 'salvo',
    colour: 0xff8a3c,
    damage: 24,
    range: 195,
    fireRate: 0.3,
    damageType: 'EXPLOSIVE',
    splash: 52,
    armourPen: 0.15,
  },
  pod_autocannon: {
    id: 'pod_autocannon',
    name: 'Vulcan Pod',
    short: 'VULCAN',
    desc: 'Rapid ballistic secondary for close-in defence.',
    cost: 60,
    kind: 'bullet',
    colour: 0x8fe36b,
    damage: 6,
    range: 120,
    fireRate: 5,
    damageType: 'BALLISTIC',
  },
  pod_arc: {
    id: 'pod_arc',
    name: 'Lightning Node',
    short: 'LIGHTNING',
    desc: 'Chaining lightning that arcs between nearby hostiles.',
    cost: 110,
    kind: 'arc',
    colour: 0x66e0ff,
    damage: 17,
    range: 135,
    fireRate: 1.1,
    damageType: 'ENERGY',
    armourPen: 0.25,
    chain: 2,
  },
  pod_laser: {
    id: 'pod_laser',
    name: 'Laser Emitter',
    short: 'LASER',
    desc: 'Continuous beam that burns a single target at long range.',
    cost: 105,
    kind: 'laser',
    colour: 0xff4a6a,
    damage: 26,
    range: 190,
    fireRate: 1.6,
    damageType: 'ENERGY',
    armourPen: 0.35,
  },
  pod_frost: {
    id: 'pod_frost',
    name: 'Frost Mortar',
    short: 'FROST',
    desc: 'Slow artillery shell that freezes a whole cluster on impact.',
    cost: 95,
    kind: 'frost',
    colour: 0x9fd8ff,
    damage: 12,
    range: 170,
    fireRate: 0.9,
    damageType: 'THERMAL',
    splash: 58,
    slowPct: 0.55,
  },
  pod_plasma: {
    id: 'pod_plasma',
    name: 'Plasma Lance',
    short: 'PLASMA',
    desc: 'Superheated plasma bolt that splashes and ignites.',
    cost: 115,
    kind: 'plasma',
    colour: 0xff5ad4,
    damage: 20,
    range: 150,
    fireRate: 1.4,
    damageType: 'THERMAL',
    splash: 42,
    burnDps: 10,
  },
  pod_cluster: {
    id: 'pod_cluster',
    name: 'Cluster Bomb Rack',
    short: 'CLUSTER',
    desc: 'Mortar shell that splits into bomblets over a wide area.',
    cost: 140,
    kind: 'cluster',
    colour: 0xffb347,
    damage: 30,
    range: 185,
    fireRate: 0.45,
    damageType: 'EXPLOSIVE',
    splash: 66,
    armourPen: 0.1,
  },
  pod_cryo: {
    id: 'pod_cryo',
    name: 'Cryo Vent',
    short: 'CRYO',
    desc: 'Freezing vent that slows everything it touches.',
    cost: 75,
    kind: 'cryo',
    colour: 0x9fd8ff,
    damage: 5,
    range: 115,
    fireRate: 3,
    damageType: 'THERMAL',
    splash: 36,
    slowPct: 0.4,
  },
  pod_uplink: {
    id: 'pod_uplink',
    name: 'Targeting Uplink',
    short: 'UPLINK',
    desc: 'Passive fire-control link: +12% range, +10% rate.',
    cost: 80,
    kind: 'support',
    colour: 0x7dce5a,
    damage: 0,
    range: 0,
    fireRate: 0,
    damageType: 'ENERGY',
    buffRate: 0.1,
    buffRange: 0.12,
  },
  pod_shield: {
    id: 'pod_shield',
    name: 'Shield Emitter',
    short: 'SHIELD',
    desc: 'Projects an aura that boosts nearby tower rate by 15%.',
    cost: 120,
    kind: 'support',
    colour: 0x5ec8ff,
    damage: 0,
    range: 0,
    fireRate: 0,
    damageType: 'ENERGY',
    auraRange: 100,
    auraRate: 0.15,
  },
};

export const SYMBIOSIS_ORDER = [
  'pod_missile',
  'pod_salvo',
  'pod_autocannon',
  'pod_arc',
  'pod_laser',
  'pod_frost',
  'pod_plasma',
  'pod_cluster',
  'pod_cryo',
  'pod_uplink',
  'pod_shield',
] as const;
